import { RumPublicApi } from '@datadog/browser-rum-core'
import type { IAuthData } from '@skedulo/horizon-types'
import { globalEventBus, EventBus } from '../../index'
import { AmplitudeService } from './amplitude-service'
import type { ErrorEventPayload } from '../../errors/types'
import type { TelemetryEventType } from '../types'

type TrackedTopics = TelemetryEventType['topic']

const ANALYTICS_EVENTS: TrackedTopics[] = [
  'horizon:component:loaded:v1',
  'horizon:template:loaded:v1',
  'horizon:analytics:custom:v1',
]

const ANALYTICS_PAYLOAD_TRANSFORMERS: {
  [Topic in TrackedTopics]?: (
    params: Extract<TelemetryEventType, { topic: Topic }>,
  ) => AnalyticsTrackParams
} = {
  'horizon:component:loaded:v1': ({ payload }) => ({
    event: 'Horizon component loaded',
    properties: { component: payload.component, package: payload.package },
  }),
  'horizon:template:loaded:v1': ({ payload: { name, extendedFrom } }) => ({
    event: 'Horizon template loaded',
    properties: { templateName: name, extendedFrom: extendedFrom },
  }),
  'horizon:analytics:custom:v1': ({ payload: { eventName, properties } }) => ({
    event: eventName,
    properties,
  }),
}

export interface AnalyticsTrackParamsBase {
  event: string
  properties?: Record<string, unknown>
}

export type AnalyticsTrackParams = AnalyticsTrackParamsBase

export class AnalyticsBroker {
  private amplitude?: AmplitudeService
  private datadog?: RumPublicApi

  constructor(private globalEventBus: EventBus<TelemetryEventType>) {
    this.initializeAnalyticsServices()

    this.globalEventBus.addListener((evt) => {
      if (evt.topic === 'core:authReady:v1') return this.identify(evt.payload)
      if (evt.topic === 'framework:error:v1') return this.logError(evt.payload)

      if (ANALYTICS_EVENTS.includes(evt.topic)) {
        const transformer = ANALYTICS_PAYLOAD_TRANSFORMERS[evt.topic]
        if (transformer) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore - We was unable to figure out a type for this that would understand
          // the event payload properties matched the callback function and the event topic
          const payload = transformer(evt)
          this.track({
            event: payload.event,
            properties: {
              ...payload.properties,
              window: { height: window.innerHeight, width: window.innerWidth },
            },
          })
        }
      }
    })
  }

  /**
   * Identifies the user to any analytics services being used
   */
  identify(payload: IAuthData) {
    const userId = payload.profile['https://api.skedulo.com/ven'].user_id

    this.amplitude?.setUser(userId)

    this.amplitude?.identifyOnce({
      userId,
      tenantId: payload.profile['https://api.skedulo.com/organization_id'],
    })

    this.amplitude?.identify({
      roles: payload.profile['https://api.skedulo.com/roles'],
    })
  }

  /**
   * Tracks an event to all registered analytics services
   */
  track(payload: AnalyticsTrackParams) {
    if (this.amplitude?.track) this.amplitude.track(payload)
  }

  /**
   * Manually send errors to Datadog
   */
  logError({ error, level, info }: ErrorEventPayload) {
    this.datadog?.addError(error, {
      skeduloPlatform: true,
      errorLevel: level,
      errorInfo: info,
    })
  }

  /**
   * Initializes any analytics services that we are sending tracking events to
   */
  initializeAnalyticsServices() {
    if (
      window.SkeduloPlatform_AmplitudeKey &&
      window.SkeduloPlatform_AmplitudeKey.length > 0
    )
      this.amplitude = new AmplitudeService(window.SkeduloPlatform_AmplitudeKey)

    // Datadog is initialised in Phoenix, so we have to use the same instance
    if (window.DD_RUM?.getInternalContext()) this.datadog = window.DD_RUM
  }
}

export const analyticsBroker = new AnalyticsBroker(
  globalEventBus as EventBus<TelemetryEventType>,
)
